import { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import AppearanceToggle from "../../components/UI/AppearanceToggle";
import { getRolePrefix, getRoleBasedRoute } from "../../utils/routeUtils";

import {
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const Sidebar = () => {
  const { roles, hasPermission, permissions } = useContext(AuthContext);
  const location = useLocation();
  const path = location.pathname;
  const [theme, setTheme] = useState(localStorage.getItem("theme"));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  // Helper function to generate role-based routes
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setTheme(localStorage.getItem("theme"));
    };

    // Listen for storage changes (if changed from other tabs)
    window.addEventListener("storage", handleStorageChange);

    // Also update on mount
    const interval = setInterval(() => {
      if (localStorage.getItem("theme") !== theme) {
        setTheme(localStorage.getItem("theme"));
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  // Auto-expand recruitment menu when on recruitment pages
  useEffect(() => {
    // console.log('Current path:', location.pathname);
    // console.log('isPayrollPath():', isPayrollPath());
    // console.log('isPayrollConfigurationPath():', isPayrollConfigurationPath());

    // Reset active menu first to ensure only one menu is active at a time
    let newActiveMenu = null;
    let newActiveSubMenu = null;

    if (isRecruitmentPath()) {
      newActiveMenu = "recruitment";
      if (
        location.pathname.includes("/interviews") ||
        location.pathname.includes("/scheduled-interviews") ||
        location.pathname.includes("/my-scheduled-interviews")
      ) {
        newActiveSubMenu = "interviews";
      }
    } else if (isPayrollPath()) {
      newActiveMenu = "payroll";

      if (isMyPayrollPath()) {
        newActiveSubMenu = "payroll-my";
      } else if (isPayrollConfigurationPath()) {
        newActiveSubMenu = "payroll-configuration";
      } else if (isPayrollSalaryPath()) {
        newActiveSubMenu = "payroll-salary";
      } else if (isPayrollProcessingPath()) {
        newActiveSubMenu = "payroll-processing";
      } else if (isPayrollReportsPath()) {
        newActiveSubMenu = "payroll-reports";
      } else if (isPayrollAuditPath()) {
        newActiveSubMenu = "payroll-audit";
      } else if (isPayrollAdminPath()) {
        newActiveSubMenu = "payroll-admin";
      }
    } else if (isMasterPath()) {
      newActiveMenu = "master";
    } else if (isAttendancePath()) {
      newActiveMenu = "attendance";
    } else if (isFileManagementPath()) {
      newActiveMenu = "file-management";
    } else if (isSettingsPath()) {
      newActiveMenu = "settings";
    } else if (isUsersPath()) {
      newActiveMenu = "users";
    } else {
      // For Overview, On-boarding, and other standalone pages, set activeMenu to null
      // This ensures only one menu is active at a time
      // Check if we're on Overview page
      const overviewPath = roles.includes("Admin")
        ? "/admin"
        : roles.includes("Manager")
          ? "/manager"
          : roles.includes("HR")
            ? "/hr"
            : roles.includes("HOD")
              ? "/hod/hod-requests"
              : "/user";
      const isOverviewPath = location.pathname === overviewPath;

      // Check if we're on On-boarding page
      const onboardingPath = getRoleBasedRouteLocal(
        "/admin/on_boarding_form_response",
      );
      const isOnboardingPath = location.pathname === onboardingPath;

      // For Overview and On-boarding pages, set activeMenu to null
      // For all other pages not matching any menu, also set to null
      newActiveMenu = null;
    }

    // Only update if the menu should change (prevents unnecessary re-renders)
    setActiveMenu(newActiveMenu);
    setActiveSubMenu(newActiveSubMenu);
  }, [location.pathname, roles]);

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);
  const toggleMenu = (menu) => {
    setActiveMenu((prev) => {
      // If clicking the same menu, close it. Otherwise, open the new menu (which closes any other open menu)
      return prev === menu ? null : menu;
    });
    setActiveSubMenu(null);
  };
  const toggleSubMenu = (subMenu) => {
    setActiveSubMenu((prev) => {
      // If clicking the same submenu, close it. Otherwise, open the new submenu (which closes any other open submenu)
      return prev === subMenu ? null : subMenu;
    });
  };
  const isActive = (path) => {
    // Check for exact match first
    if (location.pathname === path) return true;

    // Special handling for campaigns - also highlight when viewing campaign details
    if (path.includes("/campaigns")) {
      return location.pathname.startsWith(
        path.replace(/\/campaigns$/, "/campaigns/"),
      );
    }

    // Support for sub-paths (e.g., /admin/master/shifts/add should highlight /admin/master/shifts)
    // We add a trailing slash to avoid partial matches like /branch matching /branch-office
    const pathWithSlash = path.endsWith("/") ? path : `${path}/`;
    if (location.pathname.startsWith(pathWithSlash)) return true;

    return false;
  };

  // Check if a submenu item is active
  const isSubMenuActive = (path) => {
    const roleBasedPath = getRoleBasedRouteLocal(path);
    return (
      location.pathname === roleBasedPath ||
      location.pathname.startsWith(roleBasedPath + "/")
    );
  };

  // Check if current path is a recruitment-related path
  const isRecruitmentPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const recruitmentPaths = [
      `${rolePrefix}/recruitment-dashboard`,
      `${rolePrefix}/hiringrequests`,
      `${rolePrefix}/hod-requests`,
      `${rolePrefix}/recruitment-response`,
      `${rolePrefix}/campaigns`,
      `${rolePrefix}/formlist`,
      `${rolePrefix}/form_response`,
      `${rolePrefix}/candidates`,
      `${rolePrefix}/interviews`,
      `${rolePrefix}/scheduled-interviews`,
      `${rolePrefix}/my-scheduled-interviews`,
      `${rolePrefix}/application_form_response`,
      `${rolePrefix}/resignation`,
      "/admin/recruitment-dashboard",
      "/admin/hod-requests",
      "/admin/recruitment-response",
      "/admin/campaigns",
      "/admin/formlist",
      "/admin/form_response",
      "/admin/candidates",
      "/admin/interviews",
      "/admin/scheduled-interviews",
      "/admin/my-scheduled-interviews",
      "/recruitment-dashboard",
      "/hod-requests",
      "/recruitment-response",
      "/campaigns",
      "/formlist",
      "/form_response",
      "/candidates",
      "/interviews",
      "/scheduled-interviews",
      "/my-scheduled-interviews",
    ];

    // Check for exact matches
    if (recruitmentPaths.some((path) => location.pathname === path)) {
      return true;
    }

    // Check for campaign details pages (e.g., /manager/campaigns/4)
    const campaignDetailsPaths = [
      `${rolePrefix}/campaigns/`,
      "/admin/campaigns/",
      "/campaigns/",
    ];

    return campaignDetailsPaths.some((path) =>
      location.pathname.startsWith(path),
    );
  };

  // Check if current path is a file management-related path
  const isFileManagementPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const fileManagementPaths = [
      `${rolePrefix}/upload-resumes`,
      `${rolePrefix}/doc-generator`,
      `${rolePrefix}/manage-files`,
      "/admin/upload-resumes",
      "/admin/doc-generator",
      "/admin/manage-files",
      "/upload-resumes",
      "/doc-generator",
      "/manage-files",
    ];
    return fileManagementPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a settings-related path
  const isSettingsPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const settingsPaths = [
      `${rolePrefix}/roles`,
      `${rolePrefix}/permissions`,
      `${rolePrefix}/assign-permissions`,
      "/admin/roles",
      "/admin/permissions",
      "/admin/assign-permissions",
      "/roles",
      "/permissions",
      "/assign-permissions",
    ];
    return settingsPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a master-related path
  const isMasterPath = () => {
    return location.pathname.includes("/master/");
  };

  // Check if current path is a payroll-related path
  const isPayrollPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollPaths = [
      `${rolePrefix}/payroll/dashboard`,
      `${rolePrefix}/payroll/employee-details`,
      `${rolePrefix}/payroll/employment-details-management`,
      `${rolePrefix}/payroll/attendance-leave`,
      `${rolePrefix}/payroll/payroll-processing`,
      `${rolePrefix}/payroll/payslip-generation`,
      `${rolePrefix}/payroll/salary-structure`,
      `${rolePrefix}/payroll/salary-disbursement`,
      `${rolePrefix}/payroll/statutory-compliance`,
      `${rolePrefix}/payroll/reports`,
      `${rolePrefix}/payroll/settings`,

      // New nested Payroll menu routes
      `${rolePrefix}/payroll/tax-configurations`,
      `${rolePrefix}/payroll/processed-records`,
      `${rolePrefix}/payroll/approval-workflow`,
      `${rolePrefix}/payroll/monthly-reports`,
      `${rolePrefix}/payroll/department-reports`,
      `${rolePrefix}/payroll/custom-reports`,
      `${rolePrefix}/payroll/activity-logs`,
      `${rolePrefix}/payroll/history`,
      `${rolePrefix}/payroll/change-tracking`,
      `${rolePrefix}/payroll/import-export`,
      `${rolePrefix}/payroll/bulk-updates`,
      `${rolePrefix}/payroll/notifications`,
      `${rolePrefix}/payroll/backup-restore`,
      `${rolePrefix}/master/employee-salary-assignment`,

      // Payroll Configuration paths
      `${rolePrefix}/payroll/configuration/components`,
      `${rolePrefix}/payroll/configuration/salary-structures`,
      `${rolePrefix}/payroll/configuration/settings`,
      `${rolePrefix}/payroll/configuration/tax`,

      // Payroll Employee Management paths
      `${rolePrefix}/payroll/employee-management`,
      `${rolePrefix}/payroll/employees/salary-structures`,
      `${rolePrefix}/payroll/employees/bulk-updates`,
      `${rolePrefix}/payroll/employees/import-export`,
      `${rolePrefix}/payroll/employees/employee-details`,
      `${rolePrefix}/payroll/employees/employment-details-management`,

      // Payroll Processing paths
      `${rolePrefix}/payroll/processing/list`,
      `${rolePrefix}/payroll/processing/generate`,
      `${rolePrefix}/payroll/processing/records`,
      `${rolePrefix}/payroll/processing/approvals`,
      `${rolePrefix}/payroll/processing/payslips`,

      // Payroll Reports paths
      `${rolePrefix}/payroll/reports/monthly`,
      `${rolePrefix}/payroll/reports/department`,
      `${rolePrefix}/payroll/reports/statutory`,
      `${rolePrefix}/payroll/reports/custom`,

      // Payroll Audit paths
      `${rolePrefix}/payroll/audit/logs`,
      `${rolePrefix}/payroll/audit/history`,
      `${rolePrefix}/payroll/audit/changes`,

      // Payroll Admin paths
      `${rolePrefix}/payroll/admin/users`,
      `${rolePrefix}/payroll/admin/roles`,
      `${rolePrefix}/payroll/admin/notifications`,
      `${rolePrefix}/payroll/admin/backup`,

      // My Payroll (User)
      `${rolePrefix}/my-payroll/dashboard`,
      `${rolePrefix}/my-payroll/payslips`,
      `${rolePrefix}/my-payroll/salary-structure`,
      `${rolePrefix}/my-payroll/tax-documents`,
      `${rolePrefix}/my-payroll/certificates`,
      `${rolePrefix}/my-payroll/reimbursements`,
      `${rolePrefix}/payroll/new-dashboard`,
      `${rolePrefix}/payroll/export-hub`,
      `${rolePrefix}/payroll/payroll-configuration`,
      `${rolePrefix}/payroll/payroll-settings-new`,
      `${rolePrefix}/payroll/component-types-new`,
      `${rolePrefix}/payroll/tax-configurations-new`,
      `${rolePrefix}/payroll/employee-management`,
      `${rolePrefix}/payroll/salary-structures-new`,
      `${rolePrefix}/payroll/employee-salary-structure`,
      `${rolePrefix}/payroll/salary-history`,
      `${rolePrefix}/payroll/bulk-salary-update`,
      `${rolePrefix}/payroll/salary-import-export`,
      `${rolePrefix}/payroll/salary-fitment`,
      `${rolePrefix}/payroll/pending-approvals`,
      `${rolePrefix}/payroll/approval-history`,
      `${rolePrefix}/payroll/reports-management`,
      `${rolePrefix}/payroll/salary-configuration`,

      "/admin/payroll/dashboard",
      "/admin/payroll/employee-details",
      "/admin/payroll/employment-details-management",
      "/admin/payroll/attendance-leave",
      "/admin/payroll/payroll-processing",
      "/admin/payroll/payslip-generation",
      "/admin/payroll/salary-structure",
      "/admin/payroll/salary-disbursement",
      "/admin/payroll/statutory-compliance",
      "/admin/payroll/reports",
      "/admin/payroll/settings",
      // My Payroll (User)
      "/admin/payroll/my/dashboard",
      "/admin/payroll/my/payslips",
      "/admin/payroll/my/salary-structure",
      "/admin/payroll/my/tax-documents",
      "/admin/payroll/my/form16-certificates",
      "/admin/payroll/my/reimbursements",
      // New nested Payroll menu routes
      "/admin/payroll/tax-configurations",
      "/admin/payroll/processed-records",
      "/admin/payroll/approval-workflow",
      "/admin/payroll/monthly-reports",
      "/admin/payroll/department-reports",
      "/admin/payroll/custom-reports",
      "/admin/payroll/activity-logs",
      "/admin/payroll/history",
      "/admin/payroll/change-tracking",
      "/admin/payroll/import-export",
      "/admin/payroll/bulk-updates",
      "/admin/payroll/notifications",
      "/admin/payroll/backup-restore",
      "/admin/payroll/new-dashboard",
      "/admin/payroll/export-hub",
      "/admin/payroll/payroll-configuration",
      "/admin/payroll/payroll-settings-new",
      "/admin/payroll/component-types-new",
      "/admin/payroll/tax-configurations-new",
      "/admin/payroll/employee-management",
      "/admin/payroll/salary-structures-new",
      "/admin/payroll/employee-salary-structure",
      "/admin/payroll/salary-history",
      "/admin/payroll/bulk-salary-update",
      "/admin/payroll/salary-import-export",
      "/admin/payroll/salary-fitment",
      "/admin/payroll/pending-approvals",
      "/admin/payroll/approval-history",
      "/admin/payroll/reports-management",
      "/admin/payroll/salary-configuration",

      // Payroll Configuration paths
      "/admin/payroll/configuration/components",
      "/admin/payroll/configuration/salary-structures",
      "/admin/payroll/configuration/settings",
      "/admin/payroll/configuration/tax",

      // Payroll Employee Management paths
      "/admin/payroll/employee-management",
      "/admin/payroll/employees/salary-structures",
      "/admin/payroll/employees/bulk-updates",
      "/admin/payroll/employees/import-export",
      "/admin/payroll/employees/employee-details",
      "/admin/payroll/employees/employment-details-management",

      // Payroll Processing paths
      "/admin/payroll/processing/list",
      "/admin/payroll/processing/generate",
      "/admin/payroll/processing/records",
      "/admin/payroll/processing/approvals",
      "/admin/payroll/processing/payslips",

      // Payroll Reports paths
      "/admin/payroll/reports/monthly",
      "/admin/payroll/reports/department",
      "/admin/payroll/reports/statutory",
      "/admin/payroll/reports/custom",

      // Payroll Audit paths
      "/admin/payroll/audit/logs",
      "/admin/payroll/audit/history",
      "/admin/payroll/audit/changes",

      // Payroll Admin paths
      "/admin/payroll/admin/users",
      "/admin/payroll/admin/roles",
      "/admin/payroll/admin/notifications",
      "/admin/payroll/admin/backup",
      "/payroll/dashboard",
      "/payroll/employee-details",
      "/payroll/employment-details-management",
      "/payroll/attendance-leave",
      "/payroll/payroll-processing",
      "/payroll/payslip-generation",
      "/payroll/salary-structure",
      "/payroll/salary-disbursement",
      "/payroll/statutory-compliance",
      "/payroll/reports",
      "/payroll/settings",

      // My Payroll (User)
      "/payroll/my/dashboard",
      "/payroll/my/payslips",
      "/payroll/my/salary-structure",
      "/payroll/my/tax-documents",
      "/payroll/my/form16-certificates",
      "/payroll/my/reimbursements",

      // New nested Payroll menu routes
      "/payroll/tax-configurations",
      "/payroll/processed-records",
      "/payroll/approval-workflow",
      "/payroll/monthly-reports",
      "/payroll/department-reports",
      "/payroll/custom-reports",
      "/payroll/activity-logs",
      "/payroll/history",
      "/payroll/change-tracking",
      "/payroll/import-export",
      "/payroll/bulk-updates",
      "/payroll/notifications",
      "/payroll/backup-restore",

      // Payroll Configuration paths
      "/payroll/configuration/components",
      "/payroll/configuration/salary-structures",
      "/payroll/configuration/settings",
      "/payroll/configuration/tax",

      // Payroll Employee Management paths
      "/payroll/employee-management",
      "/payroll/employees/salary-structures",
      "/payroll/employees/bulk-updates",
      "/payroll/employees/import-export",
      "/payroll/employees/employee-details",
      "/payroll/employees/employment-details-management",

      // Payroll Processing paths
      "/payroll/processing/list",
      "/payroll/processing/generate",
      "/payroll/processing/records",
      "/payroll/processing/approvals",
      "/payroll/processing/payslips",

      // Payroll Reports paths
      "/payroll/reports/monthly",
      "/payroll/reports/department",
      "/payroll/reports/statutory",
      "/payroll/reports/custom",

      // Payroll Audit paths
      "/payroll/audit/logs",
      "/payroll/audit/history",
      "/payroll/audit/changes",

      // Payroll Admin paths
      "/payroll/admin/users",
      "/payroll/admin/roles",
      "/payroll/admin/notifications",
      "/payroll/admin/backup",
    ];
    return payrollPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a My Payroll-related path
  const isMyPayrollPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const myPayrollPaths = [
      `${rolePrefix}/my-payroll/dashboard`,
      `${rolePrefix}/my-payroll/payslips`,
      `${rolePrefix}/my-payroll/salary-structure`,
      `${rolePrefix}/my-payroll/tax-documents`,
      `${rolePrefix}/my-payroll/certificates`,
      `${rolePrefix}/my-payroll/reimbursements`,
      "/admin/my-payroll/dashboard",
      "/admin/my-payroll/payslips",
      "/admin/my-payroll/salary-structure",
      "/admin/my-payroll/tax-documents",
      "/admin/my-payroll/certificates",
      "/admin/my-payroll/reimbursements",
      "/my-payroll/dashboard",
      "/my-payroll/payslips",
      "/my-payroll/salary-structure",
      "/my-payroll/tax-documents",
      "/my-payroll/certificates",
      "/my-payroll/reimbursements",
    ];
    return myPayrollPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Configuration-related path
  const isPayrollConfigurationPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollConfigurationPaths = [
      `${rolePrefix}/payroll/configuration/components`,
      `${rolePrefix}/payroll/configuration/salary-structures`,
      `${rolePrefix}/payroll/configuration/settings`,
      `${rolePrefix}/payroll/configuration/tax`,
      `${rolePrefix}/payroll/payroll-configuration`,
      `${rolePrefix}/payroll/payroll-settings-new`,
      `${rolePrefix}/payroll/component-types-new`,
      `${rolePrefix}/payroll/tax-configurations-new`,
      `${rolePrefix}/payroll/salary-configuration`,
      "/admin/payroll/configuration/components",
      "/admin/payroll/configuration/salary-structures",
      "/admin/payroll/configuration/settings",
      "/admin/payroll/configuration/tax",
      "/admin/payroll/payroll-configuration",
      "/admin/payroll/payroll-settings-new",
      "/admin/payroll/component-types-new",
      "/admin/payroll/tax-configurations-new",
      "/admin/payroll/salary-configuration",
      "/payroll/configuration/components",
      "/payroll/configuration/salary-structures",
      "/payroll/configuration/settings",
      "/payroll/configuration/tax",
    ];
    return payrollConfigurationPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Salary-related path
  const isPayrollSalaryPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollSalaryPaths = [
      `${rolePrefix}/payroll/employees/salary-structures`,
      `${rolePrefix}/payroll/employees/bulk-updates`,
      `${rolePrefix}/payroll/employees/import-export`,
      `${rolePrefix}/payroll/employees/employee-details`,
      `${rolePrefix}/payroll/employees/employment-details-management`,
      `${rolePrefix}/payroll/employee-management`,
      `${rolePrefix}/payroll/salary-structures-new`,
      `${rolePrefix}/payroll/employee-salary-structure`,
      `${rolePrefix}/payroll/salary-history`,
      `${rolePrefix}/payroll/bulk-salary-update`,
      `${rolePrefix}/payroll/salary-import-export`,
      `${rolePrefix}/payroll/salary-fitment`,
      "/admin/payroll/employees/salary-structures",
      "/admin/payroll/employees/bulk-updates",
      "/admin/payroll/employees/import-export",
      "/admin/payroll/employee-management",
      "/admin/payroll/salary-structures-new",
      "/admin/payroll/employee-salary-structure",
      "/admin/payroll/salary-history",
      "/admin/payroll/bulk-salary-update",
      "/admin/payroll/salary-import-export",
      "/admin/payroll/salary-fitment",
      "/payroll/employees/salary-structures",
      "/payroll/employees/bulk-updates",
      "/payroll/employees/import-export",
      "/payroll/employees/employee-details",
      "/payroll/employees/employment-details-management",
    ];
    return payrollSalaryPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Processing-related path
  const isPayrollProcessingPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollProcessingPaths = [
      `${rolePrefix}/payroll/processing/list`,
      `${rolePrefix}/payroll/processing/generate`,
      `${rolePrefix}/payroll/processing/records`,
      `${rolePrefix}/payroll/processing/approvals`,
      `${rolePrefix}/payroll/processing/payslips`,
      `${rolePrefix}/payroll/pending-approvals`,
      `${rolePrefix}/payroll/approval-history`,
      `${rolePrefix}/payroll/reports-management`,
      "/admin/payroll/processing/list",
      "/admin/payroll/processing/generate",
      "/admin/payroll/processing/records",
      "/admin/payroll/processing/approvals",
      "/admin/payroll/processing/payslips",
      "/admin/payroll/pending-approvals",
      "/admin/payroll/approval-history",
      "/admin/payroll/reports-management",
      "/payroll/processing/list",
      "/payroll/processing/generate",
      "/payroll/processing/records",
      "/payroll/processing/approvals",
      "/payroll/processing/payslips",
    ];
    return payrollProcessingPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Reports-related path
  const isPayrollReportsPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollReportsPaths = [
      `${rolePrefix}/payroll/reports/monthly`,
      `${rolePrefix}/payroll/reports/department`,
      `${rolePrefix}/payroll/reports/statutory`,
      `${rolePrefix}/payroll/reports/custom`,
      "/admin/payroll/reports/monthly",
      "/admin/payroll/reports/department",
      "/admin/payroll/reports/statutory",
      "/admin/payroll/reports/custom",
      "/payroll/reports/monthly",
      "/payroll/reports/department",
      "/payroll/reports/statutory",
      "/payroll/reports/custom",
    ];
    return payrollReportsPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Audit-related path
  const isPayrollAuditPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollAuditPaths = [
      `${rolePrefix}/payroll/audit/logs`,
      `${rolePrefix}/payroll/audit/history`,
      `${rolePrefix}/payroll/audit/changes`,
      "/admin/payroll/audit/logs",
      "/admin/payroll/audit/history",
      "/admin/payroll/audit/changes",
      "/payroll/audit/logs",
      "/payroll/audit/history",
      "/payroll/audit/changes",
    ];
    return payrollAuditPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a Payroll Admin-related path
  const isPayrollAdminPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const payrollAdminPaths = [
      `${rolePrefix}/payroll/admin/users`,
      `${rolePrefix}/payroll/admin/roles`,
      `${rolePrefix}/payroll/admin/notifications`,
      `${rolePrefix}/payroll/admin/backup`,
      "/admin/payroll/admin/users",
      "/admin/payroll/admin/roles",
      "/admin/payroll/admin/notifications",
      "/admin/payroll/admin/backup",
      "/payroll/admin/users",
      "/payroll/admin/roles",
      "/payroll/admin/notifications",
      "/payroll/admin/backup",
    ];
    return payrollAdminPaths.some((path) => location.pathname === path);
  };

  // Check if current path is a users-related path
  const isUsersPath = () => {
    const rolePrefix = getRolePrefix(roles);
    const usersPaths = [
      `${rolePrefix}/users`,
      `${rolePrefix}/emergency-contacts`,
      "/admin/users",
      "/admin/emergency-contacts",
      "/users",
      "/emergency-contacts",
    ];
    return usersPaths.some((path) => location.pathname === path);
  };

  const isMenuActive = (menu) => {
    return activeMenu === menu;
  };

  // Check if current path is an attendance-related path
  const isAttendancePath = () => {
    const rolePrefix = getRolePrefix(roles);
    const attendancePaths = [
      `${rolePrefix}/attendance_management`,
      `${rolePrefix}/leave_page`,
      `${rolePrefix}/od`,
      `${rolePrefix}/co+`,
      `${rolePrefix}/leave-balance`,
      `${rolePrefix}/check-leave-balance`,
      `${rolePrefix}/public-calender`,
      `${rolePrefix}/attendance`,
      `${rolePrefix}/check-attendance`,
      `${rolePrefix}/attendance-upload`,
      `${rolePrefix}/mob-attendance`,
      `${rolePrefix}/swipe`,
      `${rolePrefix}/short-time-off`,
      `${rolePrefix}/shift-change`,
      `${rolePrefix}/pending-checkin`,
      `${rolePrefix}/pending-checkout`,
      `${rolePrefix}/leave-requests`,
      "/admin/attendance_management",
      "/admin/leave_page",
      "/admin/od",
      "/admin/co+",
      "/admin/leave-balance",
      "/admin/check-leave-balance",
      "/admin/public-calender",
      "/admin/attendance",
      "/admin/check-attendance",
      "/admin/attendance-upload",
      "/admin/mob-attendance",
      "/admin/swipe",
      "/admin/short-time-off",
      "/admin/shift-change",
      "/admin/pending-checkin",
      "/admin/pending-checkout",
      "/admin/leave-requests",
    ];

    return attendancePaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(path + "/"),
    );
  };

  const isOverviewPath = () => {
    const overviewPath = roles.includes("Admin")
      ? "/admin"
      : roles.includes("Manager")
        ? "/manager"
        : roles.includes("HR")
          ? "/hr"
          : roles.includes("HOD")
            ? "/hod/hod-requests"
            : "/user";
    return location.pathname === overviewPath;
  };

  const isOnboardingPath = () => {
    return (
      location.pathname ===
      getRoleBasedRouteLocal("/admin/on_boarding_form_response")
    );
  };

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className="md:hidden absolute md:top-4 top-1 left-2 mt-1 z-0 p-2 bg-blue-800 hover:bg-blue-900 hover:ring-2 ring-blue-200 transition delay-75 rounded-lg"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
      >
        <svg
          data-slot="icon"
          className="w-6 h-6 text-white"
          aria-hidden="true"
          fill="none"
          strokeWidth="1.5"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>
        </svg>
      </button>

      {/* Sidebar Drawer */}
      {/* Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black bg-opacity-75 md:hidden"></div>
      )}

      <div
        className={`fixed inset-y-0 left-0 z-20 border-r-gray-800 dark:bg-black bg-white text-gray-800 shadow-lg
          transform transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static
          h-screen max-h-screen
          ${isCollapsed ? "w-24" : "md:w-[260px] border-r-none"}
          overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-200 scrollbar-hide`}
      >
        {/* Header (Collapse + Mobile Close) */}
        <div
          className={`${
            isCollapsed ? "p-4" : "md:p-[18px] p-[22px]"
          } flex items-center justify-between md:bg-white dark:bg-black bg-white text-white`}
        >
          {isCollapsed ? (
            <div
              {...(!mobileOpen && { onClick: toggleSidebar })}
              className="flex items-center md:cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition delay-100 px-6 py-4"
            >
              <span
                className={`${
                  isCollapsed
                    ? "flex justify-center w-full text-2xl text-black dark:text-gray-400"
                    : "text-2xl font-bold tracking-wide text-black dark:text-white"
                }`}
              >
                <FaChevronRight className="w-5 h-5 dark:text-white" />
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 mx-auto">
              <div className="bg-[#2563EB] p-1 rounded-md">
                <svg
                  className="w-8 h-8 text-white"
                  data-slot="icon"
                  aria-hidden="true"
                  fill="none"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </div>
              <span
                className={`${
                  isCollapsed
                    ? "flex justify-center w-full text-2xl"
                    : "text-2xl dark:text-white text-black font-poppins font-semibold tracking-wide mr-14"
                }`}
              >
                HRMS
              </span>
            </div>
          )}

          {!isCollapsed && (
            <span
              className="hidden md:block ml-2 cursor-pointer p-3 text-black hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition delay-100"
              {...(!mobileOpen && { onClick: toggleSidebar })}
            >
              {isCollapsed ? (
                <FaChevronDown />
              ) : (
                <FaChevronLeft className="w-5 h-5 dark:text-white" />
              )}
            </span>
          )}

          {/* Close on Mobile */}
          <button
            className="md:hidden text-black dark:text-white p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition delay-100"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <FaTimes className="text-black dark:text-white" size={20} />
          </button>
        </div>

        {/* Sidebar Links */}
        <ul className="mt-6 space-y-2">
          {/* Overview */}
          {hasPermission("overview_read") && (
            <li className="px-2">
              {!isCollapsed && (
                <p className="px-3 font-poppins dark:text-white font-semibold tracking-wide mb-2">
                  GENERAL
                </p>
              )}
              <Link
                to={
                  roles.includes("Admin")
                    ? "/admin"
                    : roles.includes("Manager")
                      ? "/manager"
                      : roles.includes("HR")
                        ? "/hr"
                        : roles.includes("HOD")
                          ? "/hod/hod-requests"
                          : "/user"
                }
                className={`flex items-center gap-4 mx-2 px-4 py-2 rounded-md transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "overview" ||
                  (activeMenu === null && isOverviewPath())
                    ? "bg-[#0077FE] text-white "
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900"
                }`}
                onClick={() => {
                  setMobileOpen(false);
                  setActiveMenu("overview");
                  setActiveSubMenu(null);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-home"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
                  <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
                  <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
                </svg>
                {!isCollapsed && (
                  <span className="font-poppins font-semibold">Overview</span>
                )}
              </Link>
            </li>
          )}

          {/* On-boarding Form response */}
          {hasPermission("onboarding_read") && (
            <li className="px-3">
              <Link
                to={getRoleBasedRouteLocal("/admin/on_boarding_form_response")}
                onClick={() => {
                  setMobileOpen(false);
                  setActiveMenu("on-boarding");
                  setActiveSubMenu(null);
                }}
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "on-boarding" ||
                  (activeMenu === null && isOnboardingPath())
                    ? "bg-[#0077FE] text-white font-sans font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-user-plus"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                  <path d="M16 19h6" />
                  <path d="M19 16v6" />
                  <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
                </svg>
                {!isCollapsed && <span>On-boarding</span>}
              </Link>
            </li>
          )}

          {/* Users */}
          {hasPermission("all_users_read") && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "users" ||
                  (activeMenu === null && isUsersPath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("users")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-users"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                  <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Users
                    {activeMenu === "users" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "users" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    { label: "All Users", path: "/admin/users" },
                    {
                      label: "Emergency Contacts",
                      path: "/admin/emergency-contacts",
                    },
                  ].map((item) => (
                    <Link
                      to={getRoleBasedRouteLocal(item.path)}
                      key={item.path}
                    >
                      <li
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                          isActive(getRoleBasedRouteLocal(item.path))
                            ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                            : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                        }`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Attendance Management Section */}
          {hasPermission("daily_attendance_read") && (
            <li className="px-3">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "attendance" ||
                  (activeMenu === null && isAttendancePath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => {
                  setActiveMenu("attendance");
                  setActiveSubMenu(null);
                  setMobileOpen(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-calendar-week"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
                  <path d="M16 3v4" />
                  <path d="M8 3v4" />
                  <path d="M4 11h16" />
                  <path d="M7 14h.013" />
                  <path d="M10.01 14h.005" />
                  <path d="M13.01 14h.005" />
                  <path d="M16.015 14h.005" />
                  <path d="M13.015 17h.005" />
                  <path d="M7.01 17h.005" />
                  <path d="M10.01 17h.005" />
                </svg>
                {!isCollapsed && <span>Attendance</span>}
              </Link>
            </li>
          )}

          {/* Payroll Section */}
          {permissions.some(
            (p) => p.startsWith("payroll_") || p === "my_payroll_read",
          ) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "payroll" ||
                  (activeMenu === null && isPayrollPath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("payroll")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-paywall"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M13 21h-6a2 2 0 0 1 -2 -2v-6a2 2 0 0 1 2 -2h10" />
                  <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
                  <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
                  <path d="M21 15h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
                  <path d="M19 21v1" />
                  <path d="M19 14v1" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full font-sans font-semibold">
                    Payroll
                    {activeMenu === "payroll" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "payroll" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {/* NEW PAYROLL MODULE - Rebuilt with comprehensive API integration */}
                  {permissions.some((p) => p.startsWith("payroll_")) && (
                    <>
                      {/* Dashboard */}
                      <Link
                        to={getRoleBasedRouteLocal(
                          "/admin/payroll/new-dashboard",
                        )}
                      >
                        <li
                          onClick={() => setMobileOpen(false)}
                          className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                            isActive(
                              getRoleBasedRouteLocal(
                                "/admin/payroll/new-dashboard",
                              ),
                            )
                              ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                              : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                          }`}
                        >
                          Dashboard
                        </li>
                      </Link>
                      {/* <Link
                        to={getRoleBasedRouteLocal(
                          "/admin/payroll/employees/employee-details"
                        )}
                      >
                        <li
                          onClick={() => setMobileOpen(false)}
                          className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                            isActive(
                              getRoleBasedRouteLocal(
                                "/admin/payroll/employees/employee-details"
                              )
                            )
                              ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                              : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                          }`}
                        >
                          Employee Details
                        </li>
                      </Link>
                      <Link
                        to={getRoleBasedRouteLocal(
                          "/admin/payroll/employees/employment-details-management"
                        )}
                      >
                        <li
                          onClick={() => setMobileOpen(false)}
                          className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                            isActive(
                              getRoleBasedRouteLocal(
                                "/admin/payroll/employees/employment-details-management"
                              )
                            )
                              ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                              : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                          }`}
                        >
                          Employee Details Management
                        </li>
                      </Link> */}

                      {/* Configuration Submenu */}
                      {permissions.some((p) =>
                        [
                          "payroll_config_read",
                          "salary_config_read",
                          "payroll_settings_read",
                          "component_types_read",
                          "tax_config_read",
                        ].includes(p),
                      ) && (
                        <>
                          <div
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md cursor-pointer ${
                              [
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/payroll-configuration",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/payroll-settings-new",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/component-types-new",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/tax-configurations-new",
                                ),
                              ].some((p) => isActive(p))
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                            onClick={() =>
                              toggleSubMenu("payroll-configuration")
                            }
                          >
                            <div className="flex justify-between items-center">
                              <span>Configuration</span>
                              {activeSubMenu === "payroll-configuration" ? (
                                <FaChevronUp size={12} />
                              ) : (
                                <FaChevronDown size={12} />
                              )}
                            </div>
                          </div>
                          {activeSubMenu === "payroll-configuration" && (
                            <ul className="pl-4 ml-2 border-l-2 border-gray-300 dark:border-gray-600">
                              {[
                                /* {
                                  label: "Payroll Configuration",
                                  path: "/admin/payroll/payroll-configuration",
                                  permission: "payroll_config_read",
                                }, */
                                {
                                  label: "Salary Configuration",
                                  path: "/admin/payroll/salary-configuration",
                                  permission: "salary_config_read",
                                },
                                /* {
                              label: "Payroll Settings",
                              path: "/admin/payroll/payroll-settings-new",
                              permission: "payroll_settings_read",
                            },
                            {
                              label: "Component Types",
                              path: "/admin/payroll/component-types-new",
                              permission: "component_types_read",
                            },
                            {
                              label: "Tax Configurations",
                              path: "/admin/payroll/tax-configurations-new",
                              permission: "tax_config_read",
                            }, */
                              ]
                                .filter(
                                  (item) =>
                                    !item.permission ||
                                    hasPermission(item.permission),
                                )
                                .map((subItem) => (
                                  <Link
                                    to={getRoleBasedRouteLocal(subItem.path)}
                                    key={subItem.path}
                                  >
                                    <li
                                      onClick={() => setMobileOpen(false)}
                                      className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                                        isSubMenuActive(subItem.path)
                                          ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                          : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                                      }`}
                                    >
                                      {subItem.label}
                                    </li>
                                  </Link>
                                ))}
                            </ul>
                          )}
                        </>
                      )}

                      {/* Employee Management Link */}
                      {hasPermission("employee_mgmt_read") && (
                        <Link
                          to={getRoleBasedRouteLocal(
                            "/admin/payroll/employee-management",
                          )}
                        >
                          <li
                            onClick={() => setMobileOpen(false)}
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                              isActive(
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/employee-management",
                                ),
                              )
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                          >
                            Employee Management
                          </li>
                        </Link>
                      )}

                      {/* Processing Submenu */}
                      {permissions.some((p) =>
                        [
                          "payroll_list_read",
                          "generate_payroll_read",
                          "pending_approvals_read",
                          "approval_history_read",
                          "reports_mgmt_read",
                        ].includes(p),
                      ) && (
                        <>
                          <div
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md cursor-pointer ${
                              [
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/processing/list",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/processing/generate",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/pending-approvals",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/approval-history",
                                ),
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/reports-management",
                                ),
                              ].some((p) => isActive(p))
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                            onClick={() => toggleSubMenu("payroll-processing")}
                          >
                            <div className="flex justify-between items-center">
                              <span>Payroll Processing</span>
                              {activeSubMenu === "payroll-processing" ? (
                                <FaChevronUp size={12} />
                              ) : (
                                <FaChevronDown size={12} />
                              )}
                            </div>
                          </div>
                          {activeSubMenu === "payroll-processing" && (
                            <ul className="pl-4 ml-2 border-l-2 border-gray-300 dark:border-gray-600">
                              {[
                                {
                                  label: "Payroll List",
                                  path: "/admin/payroll/processing/list",
                                  permission: "payroll_list_read",
                                },
                                {
                                  label: "Generate Payroll",
                                  path: "/admin/payroll/processing/generate",
                                  permission: "generate_payroll_read",
                                },
                                {
                                  label: "Pending Approvals",
                                  path: "/admin/payroll/pending-approvals",
                                  permission: "pending_approvals_read",
                                },
                                {
                                  label: "Approval History",
                                  path: "/admin/payroll/approval-history",
                                  permission: "approval_history_read",
                                },
                                {
                                  label: "Reports Management",
                                  path: "/admin/payroll/reports-management",
                                  permission: "reports_mgmt_read",
                                },
                              ]
                                .filter(
                                  (item) =>
                                    !item.permission ||
                                    hasPermission(item.permission),
                                )
                                .map((subItem) => (
                                  <Link
                                    to={getRoleBasedRouteLocal(subItem.path)}
                                    key={subItem.path}
                                  >
                                    <li
                                      onClick={() => setMobileOpen(false)}
                                      className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                                        isSubMenuActive(subItem.path)
                                          ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                          : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                                      }`}
                                    >
                                      {subItem.label}
                                    </li>
                                  </Link>
                                ))}
                            </ul>
                          )}
                        </>
                      )}

                      {/* Export Hub */}
                      {hasPermission("import_export_read") && (
                        <Link
                          to={getRoleBasedRouteLocal(
                            "/admin/payroll/export-hub",
                          )}
                        >
                          <li
                            onClick={() => setMobileOpen(false)}
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                              isActive(
                                getRoleBasedRouteLocal(
                                  "/admin/payroll/export-hub",
                                ),
                              )
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                          >
                            Export Hub
                          </li>
                        </Link>
                      )}
                    </>
                  )}

                  {/* My Payroll (User/Employee) */}
                  {hasPermission("my_payroll_read") && (
                    <>
                      <Link
                        to={getRoleBasedRouteLocal(
                          "/admin/payroll/my-payrolls",
                        )}
                      >
                        <li
                          onClick={() => setMobileOpen(false)}
                          className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                            isActive(
                              getRoleBasedRouteLocal(
                                "/admin/payroll/my-payrolls",
                              ),
                            )
                              ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                              : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                          }`}
                        >
                          My Payrolls
                        </li>
                      </Link>
                    </>
                  )}

                  {/* OLD PAYROLL PAGES - COMMENTED OUT AS REQUESTED */}
                  {/* Uncomment individual pages below if needed later */}
                  {/*
                  <Link to={getRoleBasedRouteLocal('/admin/payroll/dashboard')}>
                    <li onClick={() => setMobileOpen(false)} className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${isActive(getRoleBasedRouteLocal('/admin/payroll/dashboard')) ? 'dark:bg-gray-900 bg-white dark:text-white text-blue-500' : 'text-gray-700 hover:text-blue-500 dark:hover:text-white'}`}>
                      Old Dashboard
                    </li>
                  </Link>
                  */}
                </ul>
              )}
            </li>
          )}

          {/* Recruitment */}
          {permissions.some((p) =>
            [
              "recruitment_dashboard_read",
              "hod_requests_read",
              "campaigns_read",
              "interviews_read",
              "hiring_request_read",
              "resignations_read",
              "all_candidates_read",
              "application_responses_read",
            ].includes(p),
          ) && (
            <li className="px-2 mb-2">
              {!isCollapsed && (
                <p className="px-2 font-poppins dark:text-white font-semibold tracking-wide mb-2">
                  MANAGEMENT
                </p>
              )}
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "recruitment" ||
                  (activeMenu === null && isRecruitmentPath())
                    ? "bg-[#0077FE] text-white font-sans font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("recruitment")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="27"
                  height="27"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-replace-user"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M21 11v-3c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-6m0 0l3 3m-3 -3l3 -3" />
                  <path d="M3 13.013v3c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586h6m0 0l-3 -3m3 3l-3 3" />
                  <path d="M16 16.502c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586c.53 0 1.039 -.211 1.414 -.586c.375 -.375 .586 -.884 .586 -1.414c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414z" />
                  <path d="M4 4.502c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586c.53 0 1.039 -.211 1.414 -.586c.375 -.375 .586 -.884 .586 -1.414c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414z" />
                  <path d="M21 21.499c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-2c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414" />
                  <path d="M9 9.499c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586h-2c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Recruitment
                    {activeMenu === "recruitment" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "recruitment" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    {
                      label: "Dashboard",
                      path: "/admin/recruitment-dashboard",
                      permission: "recruitment_dashboard_read",
                    },
                    {
                      label: "Hiring Request",
                      path: "/admin/hiringrequests",
                      permission: "hiring_request_read",
                    },
                    {
                      label: "HOD Requests",
                      path: "/hod-requests",
                      permission: "hod_requests_read",
                    },
                    {
                      label: "Application Responses",
                      path: "/admin/application_form_response",
                      permission: "application_responses_read",
                    },
                    {
                      label: "Campaigns",
                      path: "/admin/campaigns",
                      permission: "campaigns_read",
                    },
                    {
                      label: "All Candidates",
                      path: "/admin/candidates",
                      permission: "all_candidates_read",
                    },
                    {
                      label: "Interviews",
                      path: "/admin/interviews",
                      permission: "interviews_read",
                      hasSubmenu: true,
                    },
                    {
                      label: "Resignations",
                      path: "/admin/resignation",
                      permission: "resignations_read",
                    },
                  ]
                    .filter(
                      (item) =>
                        !item.permission || hasPermission(item.permission),
                    )
                    .map((item) =>
                      item.hasSubmenu ? (
                        <div key={item.path}>
                          <div
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md cursor-pointer ${
                              isActive(getRoleBasedRouteLocal(item.path)) ||
                              isActive(
                                getRoleBasedRouteLocal(
                                  "/admin/scheduled-interviews",
                                ),
                              ) ||
                              isActive(
                                getRoleBasedRouteLocal(
                                  "/admin/my-scheduled-interviews",
                                ),
                              )
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                            onClick={() => toggleSubMenu("interviews")}
                          >
                            <div className="flex justify-between items-center">
                              <span>{item.label}</span>
                              {activeSubMenu === "interviews" ? (
                                <FaChevronUp size={12} />
                              ) : (
                                <FaChevronDown size={12} />
                              )}
                            </div>
                          </div>
                          {activeSubMenu === "interviews" && (
                            <ul className="pl-4 ml-2 border-l-2 border-gray-300 dark:border-gray-600">
                              {(() => {
                                // For Manager, HOD, and Admin: Only show "Interviews Assigned to me"
                                if (
                                  roles.some((r) =>
                                    ["Manager", "HOD", "Admin"].includes(r),
                                  )
                                ) {
                                  return [
                                    {
                                      label: "Interviews Assigned to me",
                                      path: "/admin/my-scheduled-interviews",
                                    },
                                  ];
                                }
                                // For HR: Show "All", "Scheduled", and "Interviews Assigned to me"
                                if (roles.includes("HR")) {
                                  return [
                                    { label: "All", path: "/admin/interviews" },
                                    {
                                      label: "Scheduled",
                                      path: "/admin/scheduled-interviews",
                                    },
                                    {
                                      label: "Interviews Assigned to me",
                                      path: "/admin/my-scheduled-interviews",
                                    },
                                  ];
                                }
                                // For other roles: Show "All" and "Scheduled"
                                return [
                                  { label: "All", path: "/admin/interviews" },
                                  {
                                    label: "Scheduled",
                                    path: "/admin/scheduled-interviews",
                                  },
                                ];
                              })().map((subItem) => (
                                <Link
                                  to={getRoleBasedRouteLocal(subItem.path)}
                                  key={subItem.path}
                                >
                                  <li
                                    onClick={() => setMobileOpen(false)}
                                    className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                                      isSubMenuActive(subItem.path)
                                        ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                        : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                                    }`}
                                  >
                                    {subItem.label}
                                  </li>
                                </Link>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={getRoleBasedRouteLocal(item.path)}
                          key={item.path}
                        >
                          <li
                            onClick={() => setMobileOpen(false)}
                            className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                              isActive(getRoleBasedRouteLocal(item.path))
                                ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                                : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                            }`}
                          >
                            {item.label}
                          </li>
                        </Link>
                      ),
                    )}
                </ul>
              )}
            </li>
          )}

          {/* File Management */}
          {(hasPermission("manage_files_read") ||
            hasPermission("upload_resumes_read")) && (
            <li className="px-3">
              <div
                className={`flex items-center truncate gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "file-management" ||
                  (activeMenu === null && isFileManagementPath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("file-management")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-file-database"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M12 12.75m-4 0a4 1.75 0 1 0 8 0a4 1.75 0 1 0 -8 0" />
                  <path d="M8 12.5v3.75c0 .966 1.79 1.75 4 1.75s4 -.784 4 -1.75v-3.75" />
                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                  <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full truncate">
                    File Management
                    {activeMenu === "file-management" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "file-management" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    {
                      label: "Upload Resumes",
                      path: "/admin/upload-resumes",
                      roles: ["HR"],
                    },
                    // { label: "DOC Generator", path: "/admin/doc-generator", roles: ['HR'] },
                    {
                      label: "Manage Files",
                      path: "/admin/manage-files",
                      roles: ["HR"],
                    },
                  ].map((item) => (
                    <Link to={getRoleBasedRouteLocal(item.path)}>
                      <li
                        key={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-sans hover:bg-blue-100 dark:hover:bg-gray-900 truncate rounded-md ${
                          isActive(getRoleBasedRouteLocal(item.path))
                            ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                            : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                        }`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Settings */}
          {(hasPermission("role_settings_read") ||
            hasPermission("permission_settings_read")) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "settings" ||
                  (activeMenu === null && isSettingsPath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("settings")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="27"
                  height="27"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-settings"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                  <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Settings
                    {activeMenu === "settings" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "settings" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  <Link
                    to={getRoleBasedRouteLocal("/admin/roles")}
                    onClick={() => setMobileOpen(false)}
                  >
                    <li
                      className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                        isActive(getRoleBasedRouteLocal("/admin/roles"))
                          ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                          : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                      }`}
                    >
                      Role
                    </li>
                  </Link>
                  <Link
                    to={getRoleBasedRouteLocal("/admin/assign-permissions")}
                    onClick={() => setMobileOpen(false)}
                  >
                    <li
                      className={`py-2 px-2 my-1 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 dark:hover:bg-gray-900 rounded-md ${
                        isActive(
                          getRoleBasedRouteLocal("/admin/assign-permissions"),
                        )
                          ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                          : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                      }`}
                    >
                      Permissions
                    </li>
                  </Link>
                </ul>
              )}
            </li>
          )}

          {/* Master */}
          {permissions.some((p) => p.startsWith("master_")) && (
            <li className="px-3">
              <div
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md cursor-pointer transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "master" ||
                  (activeMenu === null && isMasterPath())
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => toggleMenu("master")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="27"
                  height="27"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-user-shield"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M6 21v-2a4 4 0 0 1 4 -4h2" />
                  <path d="M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5z" />
                  <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                </svg>
                {!isCollapsed && (
                  <span className="flex justify-between items-center w-full">
                    Master
                    {activeMenu === "master" ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </span>
                )}
              </div>
              {!isCollapsed && activeMenu === "master" && (
                <ul className="pl-6 border-l-2 border-gray-300 dark:border-gray-600 mx-2 mt-2 space-y-1">
                  {[
                    {
                      label: "Employment-Type",
                      path: "/admin/master/employment-type",
                    },
                    { label: "Departments", path: "/admin/master/departments" },
                    {
                      label: "Designations",
                      path: "/admin/master/designations",
                    },
                    { label: "Branch", path: "/admin/master/workplace" },
                    { label: "State Code", path: "/admin/master/state_code" },
                    {
                      label: "Employee Grade",
                      path: "/admin/master/employee-grades",
                    },
                    { label: "Categories", path: "/admin/master/categories" },
                    {
                      label: "Salary Grade",
                      path: "/admin/master/salary-grade",
                    },
                    { label: "Leave Type", path: "/admin/master/leave-type" },
                    { label: "Shifts", path: "/admin/master/shifts" },
                    { label: "Holidays", path: "/admin/master/holidays" },
                    {
                      label: "Leave Balance Masters",
                      path: "/admin/master/leave-balance-master",
                    },
                    {
                      label: "Forms Masters",
                      path: "/admin/master/forms-master",
                    },
                    {
                      label: "Department HODs",
                      path: "/admin/master/department-hods",
                    },
                    {
                      label: "Add Policy",
                      path: "/admin/master/add-policy",
                    },
                    // {
                    //   label: "Approver Modules",
                    //   path: "/admin/master/approver-modules",
                    // },
                    // {
                    //   label: "Approval Level",
                    //   path: "/admin/master/approval-level",
                    // },
                    // {
                    //   label: "Employee Approver",
                    //   path: "/admin/master/employee-approver",
                    // },
                    // {
                    //   label: "Leave Policy",
                    //   path: "/admin/master/leave-policy",
                    // },
                    {
                      label: "Salary Component",
                      path: "/admin/master/salary-component",
                    },
                    // { label: "Pay Structure", path: "/admin/master/pay-structure" },
                    // { label: "Eligibility Rule", path: "/admin/master/eligibility-rule" },
                  ].map((item) => (
                    <Link to={getRoleBasedRouteLocal(item.path)}>
                      <li
                        key={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`py-2 px-2 dark:text-gray-500 font-semibold font-poppins hover:bg-blue-100 my-1 dark:hover:bg-gray-900 rounded-md ${
                          isActive(getRoleBasedRouteLocal(item.path))
                            ? "dark:bg-gray-900 bg-white dark:text-white text-blue-500"
                            : "text-gray-700 hover:text-blue-500 dark:hover:text-white"
                        }`}
                      >
                        {item.label}
                      </li>
                    </Link>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Policy */}
          {hasPermission("policy_mgmt_read") && (
            <li className="px-3 mb-2">
              <Link
                to={getRoleBasedRouteLocal("/admin/policy")}
                className={`flex items-center gap-4 mx-1 px-4 py-2 rounded-md transition ${
                  isCollapsed ? "justify-center" : ""
                } ${
                  activeMenu === "policy" ||
                  (activeMenu === null &&
                    location.pathname ===
                      getRoleBasedRouteLocal("/admin/policy"))
                    ? "bg-[#0077FE] text-white font-poppins font-semibold"
                    : "hover:bg-white dark:text-gray-500 dark:hover:bg-gray-900 font-poppins font-medium"
                }`}
                onClick={() => {
                  setActiveMenu("policy");
                  setActiveSubMenu(null);
                  setMobileOpen(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-file-text"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                  <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                  <path d="M9 9h1" />
                  <path d="M9 13h6" />
                  <path d="M9 17h6" />
                </svg>
                {!isCollapsed && (
                  <span className="font-poppins font-medium">
                    Company Policies
                  </span>
                )}
              </Link>
            </li>
          )}

          {/* appearance Toggle */}
          {/* {!isCollapsed && (
            <li className="px-4 py-2">
              <AppearanceToggle />
            </li>  
          )} */}
        </ul>
      </div>
    </>
  );
};

export default Sidebar;
